/**
 * 서버 문자열을 그대로 쓴다.
 *
 * functions 쪽 HttpsError 메시지는 전부 해요체로 맞춰 놨다(sowhat-functions#8).
 * 여기서 다시 쓰지 않는 이유는 두 벌이 갈라지면 무엇이 진짜인지 알 수 없어지기 때문이다.
 * 화면이 더할 것은 "그래서 지금 뭘 하면 되는지" 한 줄뿐이다.
 *
 * **단, Firebase Auth 는 우리 서버가 아니다.** 로그인·가입 에러는 Auth SDK 가
 * 던지는데 메시지가 영문 그대로다 — 화면에 `Firebase: Error (auth/invalid-credential).`
 * 가 떴다. 우리가 못 고치는 문자열이므로 여기서만 갈아끼운다.
 */

/**
 * 로그인 실패는 **왜 실패했는지 말하지 않는다.**
 * 「그런 계정 없어요」와 「비밀번호가 틀렸어요」를 갈라 주면 어떤 이메일이 가입돼
 * 있는지 확인해 주는 꼴이 된다(비밀번호 재설정 문구를 하나로 맞춘 것과 같은 이유).
 * 요즘 Firebase 가 둘을 invalid-credential 하나로 합쳐 내려주는 것도 그래서다.
 */
const AUTH = {
  "auth/invalid-credential": ["이메일이나 비밀번호가 맞지 않아요", "둘 중 어느 쪽인지는 알려드리지 않아요. 비밀번호가 기억나지 않으면 아래에서 재설정해 주세요."],
  "auth/wrong-password": ["이메일이나 비밀번호가 맞지 않아요", "비밀번호가 기억나지 않으면 아래에서 재설정해 주세요."],
  "auth/user-not-found": ["이메일이나 비밀번호가 맞지 않아요", "비밀번호가 기억나지 않으면 아래에서 재설정해 주세요."],
  "auth/invalid-email": ["이메일 주소 형식이 아니에요", "@ 앞뒤를 다시 봐주세요."],
  "auth/missing-password": ["비밀번호를 적어주세요", null],
  "auth/weak-password": ["비밀번호가 너무 짧아요", "여섯 자 이상으로 적어주세요."],
  "auth/email-already-in-use": ["이미 쓰고 있는 이메일이에요", "그 계정의 비밀번호로 로그인해 주세요."],
  "auth/credential-already-in-use": ["이미 다른 계정에 연결된 로그인이에요", "그 계정으로 바로 로그인해 주세요."],
  "auth/too-many-requests": ["잠시 막아뒀어요", "여러 번 틀려서 그래요. 조금 뒤에 다시 해주세요."],
  "auth/network-request-failed": ["연결이 끊겼어요", "네트워크를 확인하고 다시 해주세요."],
  "auth/popup-closed-by-user": ["창이 닫혔어요", "다시 눌러주세요."],
  "auth/cancelled-popup-request": ["창이 닫혔어요", "다시 눌러주세요."],
  "auth/popup-blocked": ["브라우저가 창을 막았어요", "팝업 차단을 풀고 다시 해주세요."],
  // 이 도메인이 Firebase 인증의 승인된 도메인 목록에 없으면 팝업이 아예 안 뜬다.
  // 사용자가 할 수 있는 게 없는 설정 문제라, 우리 잘못이라고 말한다.
  "auth/unauthorized-domain": ["이 주소에서는 구글 로그인이 아직 안 열렸어요", "저희 설정 문제예요. 이메일로 들어와 주세요."],
  "auth/user-disabled": ["쓸 수 없는 계정이에요", null],
  "auth/operation-not-allowed": ["지금은 이 방법으로 로그인할 수 없어요", null],
  "auth/requires-recent-login": ["다시 로그인해 주세요", "오래 지나서 한 번 더 확인이 필요해요."],
};

const HINTS = {
  "resource-exhausted": "내일 자정에 새로 채워져요.",
  unauthenticated: "새로고침하면 다시 준비돼요.",
  "permission-denied": "로그인 상태를 확인해 주세요.",
  "failed-precondition": "화면을 새로고침하면 지금 상태부터 다시 보여드려요.",
  internal: "잠깐 몰렸을 수 있어요. 한 번 더 눌러보세요.",
  unavailable: "네트워크가 끊겼는지 확인해 주세요.",
  "deadline-exceeded": "응답이 늦어요. 한 번 더 눌러보세요.",
};

export function readError(err, fallback = "지금은 안 되네요") {
  if (!err) return { title: fallback, hint: null, code: null };

  const raw = err.code ?? null;
  if (raw?.startsWith("auth/")) {
    // 모르는 auth 코드까지 영문 메시지를 띄우진 않는다 — 코드만 작게 남긴다
    const [title, hint] = AUTH[raw] ?? ["로그인이 안 됐어요", `잠시 뒤 다시 해주세요. (${raw})`];
    return { title, hint, code: raw };
  }

  const code = raw?.replace(/^functions\//, "") ?? null;
  const title = err.message?.trim() || fallback;
  return { title, hint: HINTS[code] ?? null, code };
}

/** 한도 소진은 다른 화면으로 보내야 해서 따로 본다. */
export const isLimitReached = (err) =>
  err?.code?.replace(/^functions\//, "") === "resource-exhausted";
